import { useState } from "react";
import { BarChart3, ClipboardList, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/ui/status-pill";
import { cn } from "@/lib/utils/cn";

import {
  useGrantLicense,
  useMembersQuery,
  useRevokeLicense,
  useRevokeMember,
} from "../api";
import { apiErrorDetail } from "../lib/errors";
import { GroupRole, type PartnerGroupMembership } from "../types";

/** On-behalf data view target: which member page to open first. */
export type MemberTab = "diagnostic" | "plan";

function roleLabel(role: GroupRole, t: (k: string) => string): string {
  switch (role) {
    case GroupRole.Partner:
      return t("partners.members.rolePartner");
    case GroupRole.LocalPartner:
      return t("partners.members.roleLocalPartner");
    default:
      return t("partners.members.roleMember");
  }
}

/** Role badge tone — an identity colour per role (distinct from SD thematics). */
function roleBadgeClass(role: GroupRole): string {
  switch (role) {
    case GroupRole.Partner:
      return "bg-theme-governance-soft text-theme-governance-ink";
    case GroupRole.LocalPartner:
      return "bg-theme-economic-soft text-theme-economic-ink";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function memberName(m: PartnerGroupMembership): string {
  const name = [m.user_first_name, m.user_last_name].filter(Boolean).join(" ");
  return name || m.user_email;
}

/** Members table with per-member consultation links + a management modal. */
export function MemberTable({
  groupId,
  onViewMember,
}: {
  groupId: string;
  onViewMember: (profileId: string, name: string, tab?: MemberTab) => void;
}) {
  const { t } = useTranslation();
  const members = useMembersQuery(groupId);
  const grant = useGrantLicense(groupId);
  const revokeLicense = useRevokeLicense(groupId);
  const revokeMember = useRevokeMember(groupId);
  const [manageId, setManageId] = useState<string | null>(null);

  const onError = (err: unknown) =>
    toast.error(apiErrorDetail(err) ?? t("partners.actionFailed"));

  const busy = grant.isPending || revokeLicense.isPending || revokeMember.isPending;

  if (members.isLoading) {
    return <p className="text-sm text-muted-foreground">{t("partners.loading")}</p>;
  }

  if (members.isError) {
    return (
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-destructive">{t("partners.loadError")}</p>
        <Button variant="outline" size="sm" onClick={() => void members.refetch()}>
          {t("partners.retry")}
        </Button>
      </div>
    );
  }

  if (!members.data || members.data.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("partners.members.empty")}</p>;
  }

  const managed = members.data.find((m) => m.id === manageId) ?? null;

  const doRevokeMember = (m: PartnerGroupMembership) => {
    if (!window.confirm(t("partners.members.confirmRevokeMember", { name: memberName(m) }))) {
      return;
    }
    revokeMember.mutate(m.user, { onError, onSuccess: () => setManageId(null) });
  };

  // Shared between the desktop table and the mobile cards.
  const roleBadge = (m: PartnerGroupMembership) => (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
        roleBadgeClass(m.group_role),
      )}
    >
      {roleLabel(m.group_role, t)}
    </span>
  );

  const licenseBadge = (m: PartnerGroupMembership) =>
    m.has_license ? (
      <span className="inline-flex items-center rounded-md bg-success/10 px-2 py-0.5 text-xs font-semibold text-success">
        {t("partners.members.hasLicense")}
      </span>
    ) : m.has_external_license ? (
      <span
        title={t("partners.members.externalLicenseHint")}
        className="inline-flex items-center rounded-md bg-theme-economic-soft px-2 py-0.5 text-xs font-semibold text-theme-economic-ink"
      >
        {t("partners.members.externalLicense")}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground">
        {t("partners.members.noLicense")}
      </span>
    );

  // Résilié / Expiré, beside the licence it still holds. Muted for a
  // cancellation, destructive for a lapse — the tones of the Django chips
  // (`status-chip--inactive` / `--expired`).
  const subscriptionPill = (m: PartnerGroupMembership) =>
    m.subscription_state === "none" ? null : (
      <StatusPill tone={m.subscription_state === "cancelled" ? "muted" : "destructive"}>
        {t(`partners.members.subscriptionState.${m.subscription_state}`)}
      </StatusPill>
    );

  const memberActions = (m: PartnerGroupMembership) => {
    if (m.group_role === GroupRole.Partner) {
      return (
        <span className="text-xs text-muted-foreground">
          {t("partners.members.owner")}
        </span>
      );
    }
    const canConsult = Boolean(m.user_enterprise_profile_id) && m.has_view_consent;
    return (
      <>
        {canConsult ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <ConsultLink
              icon={<BarChart3 className="size-3.5" aria-hidden />}
              label={t("partners.members.linkResults")}
              onClick={() =>
                onViewMember(
                  m.user_enterprise_profile_id as string,
                  memberName(m),
                  "diagnostic",
                )
              }
            />
            <ConsultLink
              icon={<ClipboardList className="size-3.5" aria-hidden />}
              label={t("partners.members.linkActionPlan")}
              onClick={() =>
                onViewMember(
                  m.user_enterprise_profile_id as string,
                  memberName(m),
                  "plan",
                )
              }
            />
          </div>
        ) : (
          <span className="text-xs italic text-muted-foreground">
            {m.user_enterprise_profile_id
              ? t("partners.members.consentNotGranted")
              : t("partners.members.noSharedData")}
          </span>
        )}
        <Button variant="outline" size="sm" onClick={() => setManageId(m.id)}>
          {t("partners.members.manage")}
        </Button>
      </>
    );
  };

  return (
    <div>
      {/* Mobile: one card per member; the 4-column table squishes on a phone. */}
      <ul className="space-y-2 md:hidden">
        {members.data.map((m) => (
          <li key={m.id} className="rounded-lg border p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold">{memberName(m)}</div>
                <div className="break-words text-xs text-muted-foreground">
                  {m.user_email}
                </div>
              </div>
              {roleBadge(m)}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {licenseBadge(m)}
              {subscriptionPill(m)}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              {memberActions(m)}
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="px-3 py-2 font-semibold">
              {t("partners.members.colName")}
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              {t("partners.members.colRole")}
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              {t("partners.members.colLicense")}
            </th>
            <th scope="col" className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {members.data.map((m) => (
            <tr key={m.id} className="border-b last:border-0 align-top">
              <td className="px-3 py-3">
                <div className="font-semibold">{memberName(m)}</div>
                <div className="text-xs text-muted-foreground">{m.user_email}</div>
              </td>
              <td className="px-3 py-3">{roleBadge(m)}</td>
              <td className="px-3 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  {licenseBadge(m)}
                  {subscriptionPill(m)}
                </div>
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center justify-end gap-4">
                  {memberActions(m)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {managed && (
        <ManageModal
          member={managed}
          busy={busy}
          onClose={() => setManageId(null)}
          onGrant={() => grant.mutate(managed.user, { onError })}
          onRevokeLicense={() => revokeLicense.mutate(managed.user, { onError })}
          onRemove={() => doRevokeMember(managed)}
        />
      )}
    </div>
  );
}

function ConsultLink({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-semibold text-theme-economic-ink underline-offset-2 hover:underline"
    >
      {icon}
      {label}
    </button>
  );
}

/** Licence grant/revoke + removal, in a modal (mirrors the legacy "Gestion"). */
function ManageModal({
  member,
  busy,
  onClose,
  onGrant,
  onRevokeLicense,
  onRemove,
}: {
  member: PartnerGroupMembership;
  busy: boolean;
  onClose: () => void;
  onGrant: () => void;
  onRevokeLicense: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const name =
    [member.user_first_name, member.user_last_name].filter(Boolean).join(" ") ||
    member.user_email;
  // A partner licence is only for the group's managers — it bypasses the
  // subscription gate, so the server refuses to grant one to a plain member.
  // The legacy group-detail modal hides the section on the same condition.
  const canHoldLicence =
    member.group_role === GroupRole.Partner ||
    member.group_role === GroupRole.LocalPartner;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-xl border border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">
            {t("partners.members.manageTitle", { name })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("partners.members.close")}
            className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          {canHoldLicence && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm">
                {t("partners.members.licenceLabel")}:{" "}
                <span
                  className={
                    member.has_license
                      ? "font-semibold text-success"
                      : "text-muted-foreground"
                  }
                >
                  {member.has_license
                    ? t("partners.members.hasLicense")
                    : t("partners.members.noLicense")}
                </span>
              </span>
              {member.has_license ? (
                <Button variant="outline" size="sm" disabled={busy} onClick={onRevokeLicense}>
                  {t("partners.members.revokeLicense")}
                </Button>
              ) : (
                <Button size="sm" disabled={busy} onClick={onGrant}>
                  {t("partners.members.grant")}
                </Button>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <span className="text-sm">{t("partners.members.revokeMember")}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={onRemove}
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
            >
              {t("partners.members.revokeMember")}
            </Button>
          </div>

          {member.has_started_assessment && (
            <p className="rounded-lg bg-warning/10 px-3 py-2 text-xs text-warning">
              {t("partners.members.startedWarning")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
